import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Input } from './ui/input';
import { Search, User, FileText, TrendingUp, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Debounced cross-entity search: hits GET /api/search?q=<q> and shows a
 * grouped dropdown of clients / policies / leads. Clicking an item jumps to
 * the relevant page.
 */
const GlobalSearchBox = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ clients: [], policies: [], leads: [] });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  const runSearch = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setResults({ clients: [], policies: [], leads: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/search`, {
        params: { q: query }, withCredentials: true,
      });
      setResults({
        clients: data.clients || [],
        policies: data.policies || [],
        leads: data.leads || [],
      });
    } catch (err) {
      console.warn('Global search failed:', err?.message);
      setResults({ clients: [], policies: [], leads: [] });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(q), 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, runSearch]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const total = results.clients.length + results.policies.length + results.leads.length;
  const showDropdown = open && q.trim().length >= 2;

  const goto = (path) => {
    setOpen(false);
    setQ('');
    navigate(path);
  };

  return (
    <div ref={wrapRef} className="hidden md:flex flex-1 max-w-md ml-6 relative">
      <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      <Input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Search clients, policies, leads…"
        className="pl-9 pr-9 bg-slate-50 border-slate-200 focus-visible:ring-blue-700"
        data-testid="topbar-search-input"
      />
      {q && (
        <button
          type="button"
          onClick={() => { setQ(''); setOpen(false); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          aria-label="Clear search"
          data-testid="topbar-search-clear"
        >
          <X className="h-4 w-4" />
        </button>
      )}

      {showDropdown && (
        <div
          className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden z-40 max-h-96 overflow-y-auto"
          data-testid="global-search-dropdown"
        >
          {loading && total === 0 && (
            <div className="p-4 text-sm text-slate-500 text-center">Searching…</div>
          )}
          {!loading && total === 0 && q.trim().length >= 2 && (
            <div className="p-4 text-sm text-slate-500 text-center" data-testid="global-search-empty">
              No matches for "{q}"
            </div>
          )}

          {results.clients.length > 0 && (
            <Section title="Clients" icon={User}>
              {results.clients.map((c) => (
                <ResultRow
                  key={`c-${c.client_id}`}
                  primary={c.name}
                  secondary={`${c.primary_mobile || ''}${c.email ? ' · ' + c.email : ''}`}
                  tag={c.client_type}
                  onClick={() => { setOpen(false); setQ(''); navigate(`/clients/${c.client_id}`); }}
                  testId={`search-result-client-${c.client_id}`}
                />
              ))}
            </Section>
          )}
          {results.policies.length > 0 && (
            <Section title="Policies" icon={FileText}>
              {results.policies.map((p) => (
                <ResultRow
                  key={`p-${p.policy_id}`}
                  primary={`${p.policy_number} · ${p.insurer}`}
                  secondary={`${p.product_name || p.product_type}${p.client_name ? ' — ' + p.client_name : ''}`}
                  tag={p.status}
                  onClick={() => { setOpen(false); setQ(''); navigate(`/policies/${p.policy_id}`); }}
                  testId={`search-result-policy-${p.policy_id}`}
                />
              ))}
            </Section>
          )}
          {results.leads.length > 0 && (
            <Section title="Leads" icon={TrendingUp}>
              {results.leads.map((l) => (
                <ResultRow
                  key={`l-${l.lead_id}`}
                  primary={l.name}
                  secondary={`${l.phone || ''}${l.email ? ' · ' + l.email : ''}`}
                  tag={l.temperature || l.status}
                  onClick={() => { setOpen(false); setQ(''); navigate(`/leads?highlight=${l.lead_id}`); }}
                  testId={`search-result-lead-${l.lead_id}`}
                />
              ))}
            </Section>
          )}
        </div>
      )}
    </div>
  );
};

const Section = ({ title, icon: Icon, children }) => (
  <div className="border-b border-slate-100 last:border-b-0">
    <div className="flex items-center gap-2 px-3 py-2 bg-slate-50">
      <Icon className="h-3.5 w-3.5 text-slate-500" />
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{title}</span>
    </div>
    <div>{children}</div>
  </div>
);

const ResultRow = ({ primary, secondary, tag, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-left px-3 py-2.5 hover:bg-blue-50 flex items-center justify-between gap-3 transition-colors"
    data-testid={testId}
  >
    <div className="min-w-0">
      <p className="text-sm font-medium text-slate-900 truncate">{primary}</p>
      {secondary && <p className="text-xs text-slate-500 truncate">{secondary}</p>}
    </div>
    {tag && (
      <span className="shrink-0 text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
        {tag}
      </span>
    )}
  </button>
);

export default GlobalSearchBox;
